import React, { FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { Order } from 'models/order';
import { restorationPriceRows } from 'utils/restoration-price-rows';
import { Box, Chip, Table, TableBody, TableCell, TableContainer, TableHead, TableRow } from '@mui/material';
import { formatCurrency, formatNumber } from 'utils/format';

type PriceSpecificationSectionWithoutHighlightingProps = {
    order: Order;
    currency: string;
};

const PriceSpecificationSectionWithoutHighlighting: FC<PriceSpecificationSectionWithoutHighlightingProps> = ({
    order,
    currency,
}: PriceSpecificationSectionWithoutHighlightingProps) => {
    const { t: translate } = useTranslation(['orders', 'common']);
    const priceSpec = useMemo(() => restorationPriceRows(order?.restorations), [order]);

    return (
        <TableContainer>
            <Table size="small">
                <TableHead sx={{ borderBottom: 'none' }}>
                    <TableRow>
                        <TableCell>{translate('common:DESCRIPTION')}</TableCell>
                        <TableCell align="right">{translate('orders:QUANTITY')}</TableCell>
                        <TableCell align="right">{translate('orders:UNIT_PRICE')}</TableCell>
                        <TableCell align="right">{translate('orders:VAT')}</TableCell>
                        <TableCell align="right">{translate('orders:TOTAL')}</TableCell>
                    </TableRow>
                </TableHead>
                <TableBody>
                    {priceSpec.map((priceSpec) => (
                        <TableRow key={priceSpec._id}>
                            {priceSpec.kind === 'ACTION' ? (
                                <TableCell>
                                    <Box display="flex" alignItems="center" gap={1} width="100%">
                                        <Box
                                            sx={{
                                                minWidth: 0,
                                                overflow: 'hidden',
                                                whiteSpace: 'nowrap',
                                            }}
                                        >
                                            {priceSpec.description}
                                        </Box>

                                        {/* Right slot */}
                                        {priceSpec.isTioTandvard && (
                                            <Box flexShrink={0} display="flex" alignItems="flex-start">
                                                <Chip label={translate('orders:TIOTANDVARD_PRICE')} size="small" />
                                            </Box>
                                        )}
                                    </Box>
                                </TableCell>
                            ) : (
                                <TableCell>{priceSpec.description}</TableCell>
                            )}
                            <TableCell align="right">{formatNumber(priceSpec.quantity)}</TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(parseFloat(priceSpec.unitPrice.toFixed(2)), currency)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(parseFloat(priceSpec.vat.toFixed(2)), currency)}
                            </TableCell>
                            <TableCell sx={{ whiteSpace: 'nowrap' }} align="right">
                                {formatCurrency(priceSpec.totalWithVat, currency)}
                            </TableCell>
                        </TableRow>
                    ))}
                    <TableRow sx={{ borderTop: '2px solid #bbb' }}>
                        <TableCell colSpan={4} align="right" sx={{ borderBottom: 0 }}>
                            <strong>{translate('orders:GRAND_TOTAL')}</strong>
                        </TableCell>
                        <TableCell align="right" sx={{ whiteSpace: 'nowrap', borderBottom: 0 }}>
                            <strong>
                                {formatCurrency(
                                    (order.totalAmount?.totalPrice || 0) + (order.totalAmount?.vatAmount || 0),
                                    currency,
                                )}
                            </strong>
                        </TableCell>
                    </TableRow>
                </TableBody>
            </Table>
        </TableContainer>
    );
};
export default PriceSpecificationSectionWithoutHighlighting;
